<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <div 
            class="task-header"
            :class="[headerBgColor]"
        >
            <b-button 
                block 
                size="lg"
                v-b-toggle="`task-content-${taskId}`" 
                variant="bare-dark"
                :disabled="disableCollapse"
                class="task-header-toggle-button opacity-100 py-3 px-4 d-flex justify-content-between align-items-center"
            >  <!-- collapsed and not-collapsed classes will be added based on v-b-toggle status -->
                <div class="w-60 d-flex justify-content-start align-items-center">
                    <div 
                        :key="`${taskId}-icon`"
                        class="p-2 step-icon rounded-circle font-weight-bold d-flex justify-content-center align-items-center" 
                        :class="`bg-${colorVariant || 'primary'}-100 text-${colorVariant || 'primary'}-600`" 
                        style="height: 2rem; width: 2rem;"
                    >
                        <b-icon :icon="stateIcon"></b-icon>
                    </div>
                    <div class="ml-3 text-left">
                        <h3 class="h4 mb-0">{{ taskData.title }}</h3>
                        <!-- <div class="text-dark">{{ taskData.title }}</div>
                        <p class="small text-muted mb-0">{{ taskData.instructions }}</p> -->
                    </div>
                    <!-- <span class="chip chip-danger text-uppercase ml-4">Assigned To You</span> -->
                    <b-badge 
                        v-if="currentTuIsAssignee && isActive" 
                        :variant="isActive ? 'soft-danger' : 'soft-secondary'" 
                        class="text-uppercase ml-4"
                    >Waiting On You</b-badge>
                </div>
                <div class="w-10 text-left">
                    <!-- <status-object-badge
                        :status-object="taskData.state_dict"
                        text-uppercase
                    ></status-object-badge> -->
                </div>
                <div class="w-30">
                    <!-- <div class="d-flex align-items-center justify-content-end">
                        <span v-if="isError" class="text-danger text-uppercase font-weight-500 font-size-80 mr-2">Needs Attention</span>
                        <b-icon :icon="isError ? 'exclamation-circle' : 'check-circle'" :variant="isError ? 'danger' : 'success'"></b-icon>
                    </div> -->
                    <div 
                        v-if="!disableCollapse"
                        class="d-flex align-items-center justify-content-end"
                    >
                        <b-icon icon="chevron-expand" font-scale="2" class="when-closed text-muted"></b-icon>
                        <b-icon icon="chevron-contract" font-scale="2" class="when-open text-muted"></b-icon>
                    </div>
                </div>
            </b-button>
        </div>

        <b-collapse
            :id="`task-content-${taskId}`"
            class="border-top rounded-bottom"
            :class="[bodyBgColor]"
            visible
        >
            <transition name="fade-in" mode="out-in">
                <div 
                    v-if="loading" 
                    key="loadingTask" 
                    class="d-flex align-items-center justify-content-center min-height-300"
                >
                    <busy-spinner></busy-spinner>
                </div>

                <div 
                    v-else-if="saveMessageShowing" 
                    key="taskSavedMessage"
                    class="d-flex align-items-center justify-content-center min-height-300"
                >
                    <div class="text-center">
                        <div>
                            <b-icon 
                                icon="check" 
                                variant="success" 
                                font-scale="3" 
                                class="rounded-circle bg-success-100 p-2"
                            ></b-icon>
                        </div>
                        <h5>Saved</h5>
                    </div>
                </div>

                <div 
                    v-else 
                    key="notLoadingTask" 
                    class="row no-gutters px-4 py-4"
                >

                    <!-- <div 
                        v-if="taskData.rejection_note"
                        class="col-12"
                    >
                        <div class="border border-danger rounded bg-danger-50 px-3 py-2">
                            <p class="text-danger-darker font-weight-bold text-uppercase font-size-80 mb-2">Feedback</p>
                            <p class="mb-0">{{ taskData.rejection_note}}</p>
                        </div>
                        <hr>
                    </div> -->  <!-- Not needed now with TaskFlag -->

                    <!-- TASK FLAG AREA -->
                    <div v-if="flagsToShow.length" class="col-12">
                        <div
                            v-for="flag in flagsToShow"
                            :key="flag.id"
                            class="alert"
                            :class="`alert-${flag.colorVariant}`"
                        >
                            <p class="mb-2">
                                <span class="font-weight-bold">{{ flag.flagTitle }}</span>
                                <span class="font-italic">
                                    {{ flag.opened_by ? ` by ${flag.opened_by.full_name} ` : '' }} on {{ formatDate(flag.opened_on) }}
                                </span>
                            </p>
                            <p class="mb-0">{{ flag.opened_comment }}</p>
                            <hr class="my-2">
                            <div class="mt-2 d-flex gap-3 justify-content-between align-items-center">
                                <div class="small font-italic">  <!-- userMessage area -->
                                    {{ flag.userMessage }}
                                </div>
                                <div class="d-flex gap-3 align-items-center justify-content-end">  <!-- transition button area -->
                                    <b-button
                                        v-for="trans in flag.transitions"
                                        :key="trans.trigger"
                                        variant="subtle-dark"
                                        size="sm"
                                        @click="callTransitionTrigger(trans.trigger)"
                                    >
                                        {{ trans.label }}
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-3">

                        <div class="pr-3">
                            <!-- LINE ONE OF TASK INFO -->
                            
                            <!-- <div class="mb-4">
                                <h3>{{ taskData.title }}</h3>
                            </div> -->

                            <div 
                                v-if="taskData.instructions && !isApprovalTask" 
                                class=""
                            >
                                <p class="text-primary-darker font-weight-bold text-uppercase font-size-80 mb-2">Instructions</p>
                                <p class="mb-0 text-show-breaks">{{ taskData.instructions}}</p>
                                <hr>
                            </div>

                            <div 
                                v-if="taskData.assigned_tus && taskData.assigned_tus.length"
                                class=""
                            >
                                <p class="text-primary-darker font-weight-bold text-uppercase font-size-80 mb-2">Assigned To</p>
                                <div class="d-flex flex-column gap-2 mb-2">
                                    <div 
                                        v-for="tu in taskData.assigned_tus"
                                        :key="tu.id"
                                        class="w-100 d-flex align-items-center gap-2"
                                    >
                                        <tenant-user-badge
                                            :tu-data="tu"
                                        ></tenant-user-badge>
                                        <!-- <b-icon 
                                            v-if="loadingViewedStatus"
                                            :key="tu.id + 'viewedLoad'"
                                            name="blank"
                                            font-scale="1.2"
                                        ></b-icon> -->
                                        <div 
                                            v-b-tooltip.hover 
                                            :title="wasViewedByTuIds.includes(tu.id) ? 'Has Viewed' : 'Not Viewed'"
                                        >
                                            <b-icon 
                                                v-if="!loadingViewedStatus"
                                                :icon="wasViewedByTuIds.includes(tu.id) ? 'eye-fill' : 'eye-slash'" 
                                                style="color: var(--secondary-500);"
                                                class="animate__animated animate__fadeIn"
                                                :class="wasViewedByTuIds.includes(tu.id) ? '' : 'opacity-50'"
                                                font-scale="1.2"
                                            ></b-icon>
                                        </div>
                                        <!-- <b-icon 
                                            v-else
                                            :key="tu.id + 'notViewed'"
                                            icon="eye-slash" 
                                            variant="secondary"
                                            v-b-tooltip.hover 
                                            title="Not Viewed"
                                            class="opacity-50"
                                            font-scale="1.2"
                                        ></b-icon> -->
                                    </div>
                                </div>
                                <div class="d-flex flex-column align-items-start"> <!-- We show this if isActive and tu is owner, but disable if can't send them () -->
                                    <b-button-group>
                                        <b-button 
                                            v-if="canSendReminders" 
                                            variant="minimal-secondary"
                                            size="sm"
                                            v-b-tooltip.hover 
                                            title="Send Reminder"
                                            @click="startReminder"
                                        >
                                            <b-icon icon="alarm" variant="secondary" class="mr-1"></b-icon>
                                            Remind
                                        </b-button>
                                        <b-button 
                                            v-if="canChangeAssignedTus" 
                                            variant="minimal-secondary"
                                            size="sm"
                                            v-b-tooltip.hover 
                                            title="Change Assignee(s)"
                                            class=""
                                            @click="startChangeAssignedTus"
                                        >
                                            <b-icon icon="people" variant="secondary" class="mr-1"></b-icon>
                                            Change
                                        </b-button>
                                        <b-button 
                                            v-if="canEmailAssignedTus" 
                                            variant="minimal-secondary"
                                            size="sm"
                                            v-b-tooltip.hover 
                                            title="Email Assignee(s)"
                                            class=""
                                            @click="startEmailAssignedTus"
                                        >
                                            <b-icon icon="envelope" variant="secondary" class="mr-1"></b-icon>
                                            Email
                                        </b-button>
                                        <b-button 
                                            v-if="canAddExternalColleague" 
                                            variant="minimal-secondary"
                                            size="sm"
                                            v-b-tooltip.hover 
                                            title="Add other people from your company to complete this"
                                            class=""
                                            @click="showAddExternalColleagueModal"
                                        >
                                            <b-icon icon="person-plus" variant="secondary" class="mr-1"></b-icon>
                                            Add others from your company
                                        </b-button>
                                    </b-button-group>
                                    
                                    <b-modal
                                        v-model="addExternalColleagueModalShowing"
                                        size="md"
                                        centered
                                        title="Add others at your company"
                                        hide-footer
                                    >
                                        <p>
                                            Select the people at your company who should be assigned to complete this task.
                                        </p>
                                        <p>
                                            You can add new people by clicking on 'Add User'.  This will create an account for them, and also notify them they have been assigned this task.
                                        </p>
                                        <company-portal-user-list
                                            :company-id="$currentUserInfo.company_id"
                                            :hide-fields="['company_name', 'actions', 'tenant_roles', 'job_title', 'work_location']"
                                            class="mt-3 mb-5"
                                            selectable="multi"
                                            no-shadow
                                            :always-selected-ids="assignedTuIds"
                                            @row-selected="handleAddExternalColleagueSelected"
                                        ></company-portal-user-list>
                                        <!-- :preselected-ids="assignedTuIds" -->
                                        <!-- :always-selected-ids="[$currentUserInfo.tenant_user_id]" -->
                                        <!-- not using preselected-ids - only always-selected-ids for now, until you allow removing people by external admins - certainly possible, just needs more time/work on backend logic -->

                                        <!-- <div class="">
                                            <h6>Selections</h6>
                                            <pre>{{addExternalColleagueSelectedIds}}</pre>
                                        </div> -->

                                        <!-- show warning message if no selections? -->
                                        <b-alert :show="Boolean(addExternalColleagueError)" variant="warning">
                                            {{ addExternalColleagueError }}
                                        </b-alert>

                                        <div class="d-flex gap-3 justify-content-end">
                                            <b-button variant="minimal-secondary" size="lg" :disabled="addExternalColleagueLoading" @click="hideAddExternalColleagueModal">
                                                Cancel
                                            </b-button>
                                            <b-button variant="primary" size="lg" :disabled="addExternalColleagueLoading || (addExternalColleagueSelectedIds.length < 1)" @click="triggerAddExternalColleague">
                                                <b-spinner v-if="addExternalColleagueLoading" key="loadingSpinner" small></b-spinner>
                                                <span v-else key="addButtonText">Add New Assignees</span>
                                            </b-button>
                                        </div>
                                    
                                    </b-modal>
                                
                                </div>
                                <hr>
                            </div>
                            
                            <div v-if="deadlineText" class="">
                                <p class="text-primary-darker font-weight-bold text-uppercase font-size-80 mb-2">Deadline</p>
                                    {{ deadlineText }}
                                    <div v-if="deadlineTimezoneText" class="text-muted small">{{ deadlineTimezoneText }}</div>
                                <div class="mt-2"> 
                                    <b-button 
                                        v-if="canChangeDeadline" 
                                        variant="minimal-secondary"
                                        size="sm"
                                        @click="startChangeDeadline"
                                    >
                                        <b-icon icon="clock-history" variant="secondary" class="mr-1"></b-icon>
                                        Change Deadline
                                    </b-button>
                                </div>
                                <hr>
                            </div>
                            
                            <div 
                                v-if="taskData.state_dict" 
                                class=""
                            >
                                <p class="text-primary-darker font-weight-bold text-uppercase font-size-80 mb-2">Status</p>
                                <div>
                                    <status-object-badge
                                        :status-object="taskData.state_dict"
                                        text-uppercase
                                        font-size="0.9rem"
                                    ></status-object-badge>
                                </div>
                                <!-- <div 
                                    v-if="deadlineText"
                                    class="mt-1 text-muted font-size-80"
                                >
                                    {{ deadlineText }}
                                </div> -->
                                <div
                                    v-if="isCompleted"
                                    class="mt-1 text-muted font-size-80"
                                >
                                    <span v-if="taskData.completed_by && taskData.completed_by.full_name">
                                        by {{ taskData.completed_by.full_name }}
                                    </span>
                                    <span v-if="taskData.completed">
                                        on {{ formatDate(taskData.completed)  }}
                                    </span> 
                                </div>
                                <hr>
                            </div>
                            
                            <div 
                                v-if="showCompleteButton || extraTransitions.length"
                                class=""
                            >
                                <p class="text-primary-darker font-weight-bold text-uppercase font-size-80 mb-2">Actions</p>
                                <div v-if="showCompleteButton" class="">
                                    <b-button
                                        size="lg"
                                        :disabled="!canComplete"
                                        variant="success"
                                        class="min-width-200"
                                        @click="completeTask"
                                    >   
                                        <b-spinner v-if="loadingTransition" key="showCompleteLoading" small></b-spinner>
                                        <div v-else key="showComplete">
                                            <span>{{ completeButtonText }}</span>
                                            <b-icon icon="check-circle" class="ml-2"></b-icon>
                                        </div>
                                    </b-button>
                                </div>
                                <div 
                                    v-if="extraTransitions.length"
                                    class="mt-3 d-flex flex-column align-items-start"
                                >
                                    <b-button
                                        v-for="trans in extraTransitions"
                                        :key="trans.trigger"
                                        :variant="`minimal-secondary`"
                                        @click="callTransitionTrigger(trans.trigger)"
                                        class="text-left position-relative"
                                        style=""
                                    >
                                        <!-- <div class="font-weight-500 font-size-80">
                                            <b-link class="stretched-link text-decoration-none">{{ trans.label }}</b-link> 
                                        </div> -->
                                        <div class="font-size-80 font-weight-500 text-primary">{{ trans.label }}</div>
                                        <div class="small text-muted font-size-70">{{ trans.description }}</div>
                                    </b-button>
                                </div>
                                <!-- <hr> -->
                            </div>
                            
                            <!-- <div v-if="extraTransitions.length" class="d-flex flex-column align-items-start">
                                <p class="text-primary-darker font-weight-bold text-uppercase font-size-80 mb-2">Other Actions</p>
                                <div
                                    v-for="trans in extraTransitions"
                                    :key="trans.trigger"
                                    :variant="`light-secondary`"
                                    @click="callTransitionTrigger(trans.trigger)"
                                    class="mb-2 text-left position-relative"
                                    style=""
                                >
                                    <div class="font-weight-500 font-size-80">
                                        <b-link class="stretched-link text-decoration-none">{{ trans.label }}</b-link> 
                                    </div>
                                    <div class="small text-muted font-size-70">{{ trans.description }}</div>
                                </div>
                            </div> -->

                        </div>

                    </div>
                    <div class="col-9">  <!-- TASK CONTENT AREA -->

                        <!-- IF TASK SKIPPED -->  
                        <div 
                            v-if="isSkipped" 
                            key="skippedTaskContent" 
                            class="h-100 p-4 d-flex align-items-center justify-content-center"
                        >
                            <div class="max-width-500 w-100">
                                <div class="d-flex justify-content-center align-items-center">
                                    <div class="text-center">
                                        <p class="mb-0 font-weight-bold font-size-100">Task Skipped</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- TEMPLATE TASK CONTENT -->  
                        <!-- in workflow overview, only show template tasks if there are no other tasks it created made yet -->
                        <div 
                            v-else-if="isTemplateTask" 
                            key="templateTaskContent" 
                            class="h-100 p-4 d-flex align-items-center justify-content-center"
                        >
                            <div class="max-width-500 w-100">
                                <div class="d-flex justify-content-center align-items-center">
                                    <div class="text-center">
                                        <p class="mb-2 font-weight-bold font-size-100">This is a placeholder for auto-generated tasks</p>
                                        <p class="mb-0 font-size-80">Tasks will be created as needed when the workflow reaches this point.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- APPROVAL TASK AREA -->
                        <div v-else-if="isApprovalTask" key="taskIsApprovalTask">
                            <!-- for approval/review tasks, we'll hide the instructions in other places and put here -->
                            <div class="rounded bg-gray-100 shadow-sm border p-4 d-flex align-items-center justify-content-center">
                                <div class="max-width-500 w-100">
                                    <div v-if="isCompleted && taskData.completed_by" key="approvalCompleted" class="d-flex gap-3 flex-column justify-content-center align-items-center min-width-300">
                                        <div class="text-success font-weight-bold font-size-100 text-center">
                                            {{ `Approved on ${formatDate(taskData.completed)} by ${taskData.completed_by.full_name}` }}
                                        </div>
                                        <div v-if="approvalTaskComment" class="alert alert-success mb-0 w-100">
                                            <p class="mb-2 small font-weight-bold">Approval Comment:</p>
                                            {{ approvalTaskComment }}
                                        </div>
                                        <!-- if it is an approval task and completed, it will mean its approved -->
                                    </div>
                                    <div
                                        v-else-if="isPending" 
                                        key="approvalIsPending" 
                                        class="d-flex justify-content-center align-items-center"
                                    >
                                        <div class="text-muted font-weight-bold font-size-100 text-center">
                                            Waiting on other tasks
                                        </div>
                                    </div>
                                    <div v-else-if="currentTuIsAssignee" key="isTheApprover">
                                        <p class="text-muted font-weight-500 font-size-80 text-uppercase">Review &amp; Approval</p>
                                        <h5>{{ taskData.title }}</h5>
                                        <div class="text-show-breaks">{{ taskData.instructions }}</div>
                                        <div class="mt-5 d-flex justify-content-between align-items-center">
                                            <b-button
                                                size="lg"
                                                :disabled="!canApprovalReject"
                                                variant="sharp-danger"
                                                class="min-width-200"
                                                @click="approvalTaskReject"
                                            >   
                                                <b-spinner v-if="loadingTransition" key="rejectLoading" small></b-spinner>
                                                <div v-else key="showReject">
                                                    <span>{{rejectButtonText}}</span>
                                                    <b-icon icon="x-circle" class="ml-2"></b-icon>
                                                </div>
                                            </b-button>
                                            <b-button
                                                size="lg"
                                                :disabled="!canApprovalApprove"
                                                variant="sharp-success"
                                                class="min-width-200"
                                                @click="approvalTaskApprove"
                                            >   
                                                <b-spinner v-if="loadingTransition" key="approveLoading" small></b-spinner>
                                                <div v-else key="showApprove">
                                                    <span>{{approveButtonText}}</span>
                                                    <b-icon icon="check-circle" class="ml-2"></b-icon>
                                                </div>
                                            </b-button>
                                        </div>
                                    </div>
                                    <div v-else key="notTheApproverNotCompleted" class="d-flex justify-content-center align-items-center">
                                        <div class="text-muted font-weight-bold font-size-100 text-center">
                                            Waiting on Approval
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="approvalContentSchema" class="w-100 rounded bg-gray-100 shadow-sm border p-4 mt-3">
                                <!-- EXTRA CONTENT SHOWN FOR APPROVAL backend handles logic of when/who should see it -->
                                <workflow-form-formulate
                                    ref="approvalContentForm"
                                    :schema="approvalContentSchema"
                                    :values="{}"
                                    :task-id="taskData.id"
                                    :name="`${taskData.id}ApprovalContentForm`"
                                    :preview-mode="true"
                                ></workflow-form-formulate>
                            </div>
                        </div>

                        <!-- FORM TASK AREA -->
                        <div v-else-if="hasFormSchema" key="taskHasFormSchema" class="rounded bg-gray-100 shadow-sm border px-4 pb-4 pt-3">
                            <div class="mb-2 d-flex justify-content-between align-items-end">
                                <div>
                                    <transition name="fade-fast" mode="out-in">
                                        <div v-if="hasReport">
                                        
                                            <b-button
                                                v-if="formioFormDisplayReport"
                                                key="formioViewingAsReport"
                                                variant="gray-secondary"
                                                @click="formioFormDisplayReport = !formioFormDisplayReport"
                                            >
                                                <b-icon icon="ui-radios" class="mr-2"></b-icon>
                                                View as Form
                                            </b-button>
                                            <b-button
                                                v-else
                                                key="formioViewingAsForm"
                                                variant="gray-secondary"
                                                @click="formioFormDisplayReport = !formioFormDisplayReport"
                                            >
                                                <b-icon icon="file-earmark-text" class="mr-2"></b-icon>
                                                View as PDF
                                            </b-button>
                                        </div>    
                                    </transition>
                                    
                                </div>
                                <div class="text-right">
                                    <span class="font-size-70 text-muted text-uppercase mr-2">Form Number</span>
                                    {{ taskData.task_number }}
                                </div>
                            </div>
                            <div v-if="!hasLocalTaskData" key="waitingOnLocalTaskData" class="w-100 min-height-200 d-flex justify-content-center align-items-center">
                                <div class="text-muted text-center">Loading form...</div>
                            </div>
                            <!-- Added v-if based on hasLocalTaskData -- tells us if initial load is done so we can fetch form stuff... otherwise it fetches form stuff once from task prop, then AGAIN when localTaskData loads -->
                            <workflow-form-formulate
                                ref="formulateForm"
                                v-if="isFormulateForm && hasLocalTaskData"
                                key="formulateSchemaForm"
                                :schema="taskData.form_schema"
                                :values="taskData.submission_data"
                                :task-id="taskData.id"
                                :name="`${taskData.id}TaskForm`"
                                :disabled="formDisabled"
                                @is-valid="handleFormulateValidationStatus"
                                @submitted="handleFormulateSubmitted"
                            ></workflow-form-formulate>  <!-- :values="taskData.data" -->

                            <workflow-form-formio
                                ref="formioForm"
                                v-if="isFormioForm && hasLocalTaskData"
                                :key="`formioSchemaForm-${formioFormDisplayMode}`"
                                :task-id="taskData.id"
                                :display-mode="formioFormDisplayMode"
                                @is-valid="handleFormioValidationStatus"
                                @complete-form-submitted="handleFormioSubmitted"
                                @partial-form-submitted="handleFormioSubmitted"
                                @draft-form-submitted="handleFormioSubmitted"
                            ></workflow-form-formio>

                            <!-- <b-link 
                                v-if="$currentUserInfo.user_is_staff && taskData.form_version_urls && taskData.form_version_urls.admin_builder" 
                                :href="taskData.form_version_urls.admin_builder"
                                target="_blank"
                                class="small"
                            >Admin Editor</b-link> -->
                        </div>

                        <!-- Featured WORKFLOW transitions area -->
                        <div v-if="isWorkflowTransitionTask" class="h-100 p-4">
                            <!-- <pre>{{ featuredWorkflowTransitions}}</pre> -->
                            <div v-if="isActive" class="min-height-200">
                                <div v-if="currentTuIsAssignee" class="d-flex flex-column gap-5">
                                    <div 
                                        v-for="trans in featuredWorkflowTransitions" 
                                        :key="JSON.stringify(trans)"
                                        class="border rounded-lg bg-gray-100 px-4 py-3 w-100 d-flex gap-4 align-items-center justify-content-between"
                                    >
                                        <div class="">
                                            <h5>{{ trans.label}}</h5>
                                            <p class="text-muted mb-0">{{ trans.description }}</p>
                                        </div>
                                        <div class="">
                                            <b-button 
                                                variant="sharp-primary"
                                                size="lg"
                                                :disabled="loadingTransition || loadingWorkflowTransition"
                                                @click="callWorkflowTransitionTrigger(trans.trigger)"
                                            >Go &rarr;</b-button>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="h-100 d-flex justify-content-center align-items-center">
                                    <div class="text-muted font-weight-bold font-size-100 text-center">
                                        <b-icon variant="gray-300" icon="hourglass-split" font-scale="3" class="d-block w-100 mb-2"></b-icon>
                                        Waiting on assigned user to complete this.
                                    </div>
                                </div>
                            </div>
                            <div v-else class="min-height-200 d-flex justify-content-center align-items-center">
                                <div class="text-muted font-weight-bold font-size-100 text-center">
                                    {{ isPending ? 'Waiting on previous steps' : (isCompleted ? 'Already Completed' : 'Check Status') }}
                                </div>
                            </div>
                        </div>

                        <!-- EXTRA CONTENT AT BOTTOM OF TASK AREA -->
                        <div v-if="bottomContentSchema" class="w-100 mt-4">
                            <workflow-form-formulate
                                ref="bottomContentForm"
                                :schema="bottomContentSchema"
                                :values="{}"
                                :task-id="taskData.id"
                                :name="`${taskData.id}BottomContentForm`"
                                :preview-mode="true"
                            ></workflow-form-formulate>
                        </div>

                    </div>
                </div>

            </transition>
        </b-collapse>

        <!-- TASK STATE MACHINE TRANSITIONS -->
        <state-machine-transitions
            ref="transitionsComponent"
            @state-change="onStateChange"
            @loading="handleStateMachineLoading"
            @cancel="onStateChangeCancel"
        ></state-machine-transitions>

        <!-- WORKFLOW STATE MACHINE TRANSITIONS -->
        <state-machine-transitions
            ref="workflowTransitionsComponent"
            @state-change="afterWorkflowTransitionStateChange"
            @loading="handleWorkflowStateMachineLoading"
        ></state-machine-transitions>
        
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
//import MultiPageFormPage from '../formium/MultiPageFormPage.vue';
import TenantUserBadge from '../functional-comps/TenantUserBadge.vue';
import WorkflowFormFormulate from './WorkflowFormFormulate.vue';
//import FormioForm from './FormioForm.vue';
import { FormatDateHelper, HumanizeDateTimeHelper } from '../../js/filters-helpers';
import { DateTime } from 'luxon';

export default {
    name: 'WorkflowTask',
    components: {
        //MultiPageFormPage,
        TenantUserBadge,
        WorkflowFormFormulate,
        //FormioForm,
    },
    mixins: [
      errorHandling,
      currentUserInfoMixin,
    ],
    props: {
        taskId: {
            type: String,
            required: true,
        },
        task: {  // pass in the full task object from parent if you have it -- we'll use it until we get localTaskData, but make sure schema is same!!!
            type: Object,
            required: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            loadingTransition: false,
            loadingWorkflowTransition: false,  // we have 2 types of transitions, sometimes called sequentially, must keep these separate
            saveMessageShowing: false,
            //taskData: {},  // moved to computed, where we use EITHER localTaskData or task object from prop
            localTaskData: null,  // task data from API - set to null until you have it so you pull data from 'task' prop instead
            completeTriggerName: 'go_complete_task',  // used multiple places so put here
            reminderTriggerName: 'go_send_reminder',  // used multiple places
            changeDeadlineTriggerName: 'go_change_deadline', 
            approvalTaskApproveTrigger: 'go_approval_task_approval',  // will show special button
            approvalTaskRejectTrigger: 'go_approval_task_rejection',  // will show special button
            changeAssignedTusTriggerName: 'go_change_assigned_tus',  // will show special button
            emailAssignedTusTriggerName: 'go_email_assigned_tus', // will show special button
            addExternalColleagueTriggerName: 'go_add_external_colleague',  // for external users to add their colleagues, brings up modal for selecting/adding them
            addExternalColleagueModalShowing: false,  // v-model to show the modal
            addExternalColleagueSelectedIds: [],  // array of TU IDs that are selected from add external colleague modal
            addExternalColleagueError: '',  // keep it empty to mean no error, or put string message here to show error
            addExternalColleagueLoading: false,  // loading flag when calling this transition
            // FORMS
            formulateFormIsValid: null,  // update to true/false in event handler
            formioFormIsValid: null,  // update to true/false in event handler method
            formioFormDisplayReport: false,  // if toggle true, it will make workflow-formio-form display the report version
            waitingOnFormSubmission: false,  // set true when this component calls something on child form component to submit its stuff, so when event comes back from child we know we were expecting it, will not just simply reload the data
            // task viewed status
            loadingViewedStatus: false,  // loading flag for this
            wasViewedByTuIds: [],  // a list/array of TU.ids who have viewed this - from API
        }
    },
    computed: {
        isInWorkflowView() {
            // is this task shown as part of overall workflow view?  check this.$parent.$options.name
            // might want a prop for this to be more flexible
            // we'll show things a bit differently if NOT in workflow view (task only) - no collapse, etc.
            const workflowComponentNames = ['WorkflowOverview'];
            const parentName = this.$parent.$options.name;
            return !!parentName && workflowComponentNames.includes(parentName);
        },
        disableCollapse() {
            // turn off the collapse features?  Do this if you're not in Workflow view, and/or if you only have one task in the step
            return !this.isInWorkflowView;
            // TODO - figure out how to disable this if you only have one task in the step
        },
        taskData() {
            // we'll return localTaskData if we have it... otherwise task from parent... see if this works...
            return this.localTaskData ?? this.task ?? {};
        },
        hasLocalTaskData() {
            return !!this.localTaskData;
        },
        workflowRunId() {
            return this.localTaskData?.workflow_id ?? this.task.workflow_id ?? '';
        },
        hasFormSchema() {
            return !!this.taskData?.form_schema;
        },
        formSchemaType() {  // options are 'vueformulate' and 'formio'
            return this.taskData?.form_schema_type ?? '';
        },
        isFormulateForm() {
            return this.taskData?.form_schema_type === 'vueformulate';
        },
        isFormioForm() {
            return this.taskData?.form_schema_type === 'formio';
        },
        isPending() {
            return this.taskData?.sm_state === 'pending';
        },
        isActive() {
            return this.taskData?.sm_state === 'active';
        },
        isCompleted() {
            return this.taskData?.sm_state === 'completed';
        },
        isCancelled() {
            return this.taskData?.sm_state === 'cancelled';
        },
        isSkipped() {
            return this.taskData?.sm_state === 'skipped';
        },
        isExempt() {
            return this.taskData?.sm_state === 'exempt';  // for now only for template_tasks - can use isTemplateTask
        },
        isTemplateTask() {
            return !!this.taskData?.is_template_task;
        },
        autoFields() {
            return this.taskData?.auto_fields ?? [];
        },
        deadlineLuxon() {
            let luxonDeadline = null;  // default first
            if (this.isActive) {
                if (this.taskData.current_deadline) {
                    const deadlineIso = this.taskData?.current_deadline ?? '';
                    luxonDeadline = deadlineIso ? DateTime.fromISO(deadlineIso) : null;
                }
            }
            return luxonDeadline;
        },
        deadlineText() {
            // determines what to show in the deadline area (if anything) based on whether its active or not
            // only active tasks actually have a deadline set
            // pending tasks have a deadline_days (maybe)
            if (this.isActive) {
                if (!this.taskData.current_deadline) return 'Not Set';
                const luxonDeadline = this.deadlineLuxon;
                const deadline = (!!luxonDeadline && luxonDeadline.isValid) ? luxonDeadline.toLocaleString(DateTime.DATETIME_SHORT) : '';
                //const deadline = this.formatDate(this.taskData?.current_deadline);
                const howFarAway = HumanizeDateTimeHelper(this.taskData.current_deadline);
                return `${deadline} (${howFarAway})`;
            } else if (this.isPending) {
                // TODO - add something if you have deadline_kwargs ?
                return this.taskData?.deadline_days ? `${this.taskData.deadline_days} days after previous tasks are completed` : '';
            } else {
                // if its already complete or cancelled, just return empty string here, we won't show deadline at all
                return '';
            }
        },
        deadlineTimezoneText() {
            return this.deadlineLuxon ? this.deadlineLuxon.toFormat('ZZZZZ') : '';
        },
        bodyBgColor() {
            // bg color of main task collapse content area
            if (this.isTemplateTask) return 'bg-warning-50';
            if (this.isSkipped) return 'bg-gray-150';
            return 'bg-white';
        },
        headerBgColor() {
            // bg color of the collapse header
            if (this.isTemplateTask) return 'bg-warning-100';
            if (this.isSkipped) return 'bg-gray-150';  // keep same
            return 'bg-gray-100';
        },
        colorVariant() {
            return this.taskData?.state_dict?.color_variant;
        },
        currentState() {
            return this.taskData?.sm_state;
        },
        stateIcon() {
            // can pull from state_dict, OR from validation status???
            return this.taskData?.state_dict?.icon;
        },
        isError() {
            // ??? if current tu can edit, but form is not valid, show this???
            return false;  // TODO 
        },
        formDisabled() {
            // check a bunch of things to see if the form should be disabled
            // is it started or completed?  
            // is current TU one of the assignees?
            // using canEditDataNow is OK, but we can use canTuSubmitForm which checks TU on the backend for us
            // const assigneeCanEdit = this.canEditDataNow // OLD this.taskData?.state_dict?.assignee_can_edit;
            // const formActive = assigneeCanEdit && this.currentTuIsAssignee;
            const formActive = this.canTuSubmitForm;
            return !formActive;
        },
        assignedTuIds() {
            const assignedTus = this.taskData?.assigned_tus;
            if (assignedTus && Array.isArray(assignedTus)) {
                return this.taskData.assigned_tus.map(tu => tu.id);
            } else {
                return []
            }
        },
        currentTuIsAssignee() {
            return this.assignedTuIds.includes(this.$currentUserInfo.tenant_user_id);
        },
        currentTuIsWorkflowOwner() {
            // we might only have workflow_owner_id in the task data from API
            return !!this.taskData?.workflow_owner_id && (this.taskData.workflow_owner_id === this.$currentUserInfo.tenant_user_id);
        },
        canEditDataNow() {
            // pulls from taskData.can_edit_data_now - only applies to assigned TUs
            // can also check .can_tu_submit_form - that is for the current TU
            return this.taskData.can_edit_data_now ?? false;
        },
        canTuSubmitForm() {
            // use this instead of canEditDataNow - they are similar, but canTuSubmitForm checks current TU on backend
            // but this ONLY applies if there is a form -- if not a form task, returns false
            return this.taskData.can_tu_submit_form ?? false;
        },
        showCompleteButton() {
            // only show for assigned_tus -- separate one to figure out if we should enable it
            if (this.taskData.is_approval_task || this.taskData.hide_complete_button) {
                return false;  // hide the NORMAL complete button for approval tasks
            }
            const tuCanComplete = this.currentTuIsAssignee || this.canComplete;  // also show if canComplete is true to allow for workflow owner to see it - probably not needed anymore... we have separate transition for workflow owner
            return tuCanComplete && !(this.isCompleted || this.isCancelled);
        },
        completeButtonText() {
            // from 'complete_button_text' from API (uses model method)
            // maybe set specifically null to hide it?
            return this.taskData.complete_button_text ?? '';
        },
        approveButtonText() {
            return this.taskData.approve_button_text ?? '';
        },
        rejectButtonText() {
            return this.taskData.reject_button_text ?? '';
        },
        canComplete() {
            // figure out if we can complete this
            if (this.isFormioForm && !this.formioFormIsValid) {
                return false;  // add others like this for different task types
            }
            if (this.isFormulateForm && !this.formulateFormIsValid) {
                return false;
            }
            return this.availableTriggers.includes(this.completeTriggerName);
        },
        // showSendReminders() {  // TODO - change this or just check canSendReminders instead - this 
        //     // we SHOW button/link it if TU is owner and state is active - but might be disabled if reminder was recently sent, for example
        //     return this.isActive && this.currentTuIsWorkflowOwner;
        // },
        canChangeAssignedTus() {
            return this.availableTriggers.includes(this.changeAssignedTusTriggerName);
        },
        canEmailAssignedTus() {
            return this.availableTriggers.includes(this.emailAssignedTusTriggerName);
        },
        canAddExternalColleague() {
            return this.availableTriggers.includes(this.addExternalColleagueTriggerName);
        },
        canSendReminders() {
            // remember, there is a 'wait time' of ~15 mins after a reminder is sent -- we use this to disable send reminders link/button when in active state
            return this.availableTriggers.includes(this.reminderTriggerName);
        },
        canChangeDeadline() {
            return this.availableTriggers.includes(this.changeDeadlineTriggerName);
        },
        isApprovalTask() {
            return this.taskData?.is_approval_task;
        },
        approvalTaskComment() {
            return this.taskData?.submission_data?.comment || '';
        },
        canApprovalApprove() {
            return this.availableTriggers.includes(this.approvalTaskApproveTrigger);
        },
        canApprovalReject() {
            return this.availableTriggers.includes(this.approvalTaskRejectTrigger);
        },
        approvalContentSchema() {
            // formulate schema to render as read-only for approval content  - should be array to feed to Formulate schema, or null of no content
            return this.taskData?.approval_content_schema ? this.taskData.approval_content_schema : null; 
        },
        bottomContentSchema() {
            // formulate schema to render as read-only to show extra stuff at bottom of task area - should be array to feed to Formulate schema, or null if no content to hide the div
            return this.taskData?.bottom_content_schema ? this.taskData.bottom_content_schema : null;
        },
        availableTriggers() {
            // look at task possible_transitions and get list of trigger names that are available
            // use this to show/hide things like complete button, etc.
            const possibleTransitions = this.taskData?.possible_transitions;
            if (possibleTransitions && Array.isArray(possibleTransitions) && (possibleTransitions.length > 0)) {
                const allTriggers = possibleTransitions.map(trans => trans.trigger);
                return allTriggers;
            } else {
                return []
            }
        },
        extraTransitions() {
            // return a filtered list of possibleTransitions to SHOW in the action list
            // on SOME of the transitions, you have 'ui_data' key -- an object with more keys about where/how to show this transition
            // inside 'ui_data' you have 'action_list_hidden' -- if true, then do not include it here 
            const filterOutTriggers = [
                this.completeTriggerName, 
                this.reminderTriggerName,
                this.approvalTaskApproveTrigger,
                this.approvalTaskRejectTrigger,
                this.changeDeadlineTriggerName,
                this.changeAssignedTusTriggerName,
                this.emailAssignedTusTriggerName,
                this.addExternalColleagueTriggerName,
            ];  // maybe more later
            const possibleTrans = this.taskData.possible_transitions;
            if (possibleTrans && Array.isArray(possibleTrans)) {
                // gotta and the 2 together... not manually hidden, and not set to hidden in transition kwargs
                return possibleTrans.filter(trans => (!filterOutTriggers.includes(trans.trigger) && !trans.ui_data?.action_list_hidden));
            } else {
                return []
            }
        },
        flagsToShow() {
            // take open_flags, then get any transitions that relate to this flag
            // those transitions will have 'ui_data.flag_key': 'dispute' or whatever the flag.key is
            const openFlags = this.taskData?.open_flags;
            /*
            const possibleTransitions = this.taskData?.possible_transitions || [];
            // define a flag class????  or just keep it here...
            const makeFlag = function(flagObj) {
                const transitions = possibleTransitions.filter(trans => (trans.ui_data?.flag_key === flagObj.key));
                let userMessage = '';
                let colorVariant = 'warning';
                if (flagObj.key === 'dispute') {
                    colorVariant = 'warning';
                    userMessage = this.currentTuIsWorkflowOwner ?
                                    'As workflow owner, you can either make changes to this issue, or reject this dispute.' : 
                                    'Waiting on action from the workflow owner';
                } else if (flagObj.key === 'rejection') {
                    colorVariant = 'danger';
                    if (this.taskData?.is_approval_task) {
                        userMessage = this.currentTuIsAssignee ?
                                    'A later approver has rejected this.  Review their comments and reject earlier tasks if needed.' : 
                                    'Waiting on action from task assignee';
                    } else {
                        userMessage = this.currentTuIsAssignee ?
                                    'Please modify your submission and re-submit when ready.' : 
                                    'Waiting on action from task assignee';
                    }
                }
                return {...flagObj, transitions, colorVariant, userMessage }
            }
            */
            let builtFlags = [];
            if (openFlags && Array.isArray(openFlags)) {
                builtFlags = openFlags.map(flag => this.makeFlag(flag));  // with transitions added in
            }
            return builtFlags;
        },
        isWorkflowTransitionTask() {
            // another special kind of task - shows extra transitions to call, and if they are successful, also completes this task
            return this.taskData?.is_workflow_transition_task;
        },
        featuredWorkflowTransitions() {
            // WORKFLOW-level transitions, not task level - likely only showing if task.is_workflow_transition_task
            return this.taskData?.featured_workflow_transitions ?? [];
        },
        hasReport() {
            // just hard-coding some things for now -- it SHOULD have a report for this
            // July 18 2022 - disabling these front-end generated PDFs for now
            const showReports = false;  // forcing it to not show
            const hasMatchingKey = this.taskData.key === 'complete_car';
            const isCompleted = this.isCompleted;
            return showReports && hasMatchingKey && isCompleted && this.isFormioForm && this.hasLocalTaskData;
        },
        formioFormDisplayMode() {
            // should be 'report' or 'input' based on this.formioFormDisplayReport boolean (in data)
            return this.formioFormDisplayReport ? 'report' : 'input';
        },
    },
    async mounted() {
        await this.getDetail();
        this.reportTaskViewed();  // don't await it here... let it happen quietly as you move along
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getDetail() {  // call on initial load, or to refresh
            console.log('starting getDetail');
            this.loading = true;
            try {
                //const { data } = await suppliosApi.workflowTask.retrieve(this.taskId);  // removed this endpoint - only at get_detail now
                const { data } = await suppliosApi.workflowTask.get_detail(this.taskId);
                this.localTaskData = Object.assign({}, data);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        async reportTaskViewed() {
            // responds with list of TU IDs for any TU who has viewed it
            this.loadingViewedStatus = true;
            try {
                const { data } = await suppliosApi.workflowTask.report_task_viewed(this.taskId);
                this.wasViewedByTuIds = data;
            } catch (err) {
                console.error(err);  // don't use apiErrorHandler -- just fail quietly
            }
            this.loadingViewedStatus = false;
        },
        async showSaved(duration) {
            console.log('starting showSaved');
            const finalDuration = duration ?? 2000;
            this.saveMessageShowing = true;
            return setTimeout(() => {
                this.saveMessageShowing = false;
            }, finalDuration);
        },
        async handleFormulateSubmitted(submitData) {
            // submitData will be data that came back from API after the submit -- should be all the form data, but maybe cleaned, calculated, etc.
            console.log('from handleFormulateSubmitted', submitData);
            // we should refresh the task or something... state of task or step may have changed
            // we'll introduce a delay by showing success message or something
            //await this.showSaved(2000);
            if (this.waitingOnFormSubmission) {
                this.waitingOnFormSubmission = false;  // turn it off again so we're ready for next one
                return;  // then bail... no reload for this
            }
            this.saveMessageShowing = true;
            setTimeout(() => {
                this.saveMessageShowing = false;
                this.getDetail();  // reload it
            }, 2000);
        },
        async submitFormulateForm() {
            if (!this.isFormulateForm) {
                return false;  // make sure its a formulate form task!
            }
            const formulateForm = this.$refs['formulateForm'];
            if (!formulateForm) {
                return false;
            }
            this.waitingOnFormSubmission = true;  // a flag to tell other event handler that we were the one who called this, so it doesn't reload everything automatically
            const submitSuccess = await formulateForm.submitFromParent();  // THIS IS ASYNC FUNCTION
            return submitSuccess;
        },
        handleFormulateValidationStatus(isValid) {
            this.formulateFormIsValid = isValid;
        },
        handleFormioChanged(submissionData) {
            console.log('got new submission data from formio form', submissionData);
            // save in here?  or in child component?
        },
        handleFormioValidationStatus(isValid) {
            this.formioFormIsValid = isValid;  // boolean
        },
        handleFormioSubmitted() {  // payload is new taskData object
            // called when formio component does a submission - payload is the new task data (full task object)
            // for now we will just use same behavior for all types of submits: complete, partial, draft
            // we will reload everything UNLESS this component was the one who called it
            // we'll know that by setting a flag waitingOnFormSubmission=True
            if (this.waitingOnFormSubmission) {
                this.waitingOnFormSubmission = false;  // turn it off again so we're ready for next one
                return;  // then bail... no reload for this
            }
            // otherwise, we have to reload 
            this.getDetail();
        },
        async submitFormioForm() {
            // calls submitForm on workflow-form-formio component using ref - its ASYNC, returns boolean with status
            if (!this.isFormioForm) {
                return false;  // make sure its a formio form task!
            }
            const formioForm = this.$refs['formioForm'];
            if (!formioForm) {
                return false;
            }
            this.waitingOnFormSubmission = true;  // a flag to tell other event handler that we were the one who called this, so it doesn't reload everything automatically
            const submitSuccess = await formioForm.submitOnTaskCompleted();  // THIS IS ASYNC FUNCTION
            return submitSuccess;
        },
        /* async callTransition(transObj) {
            // call a transition -- pass in the full transition object
            console.log('call this transition', transObj);
            const triggerName = transObj.trigger;
            return await this.callTransitionTrigger(triggerName);
        },
        async callTransitionTrigger(triggerName) {
            this.loadingTransition = true;
            try {
                const postData = {trigger: triggerName};  // anything else??? send along any other kwargs needed for transition
                const transResp = await suppliosApi.workflowTask.call_transition(this.taskId, postData);
                // the entire task instance is returned 
                const newTaskData = transResp.data;
                console.log(newTaskData);
                // we will just reload things to update
                this.getDetail();
                // AND emit something to parent telling them they should probably update because state-changed
                this.$emit('state-changed', newTaskData);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingTransition = false;
        }, */
        startReminder() {
            // start process of sending a reminder email - do it with transitions
            if (!this.canSendReminders) return;
            return this.callTransitionTrigger(this.reminderTriggerName);
        },
        startChangeDeadline() {
            // start process of change deadline transition - do it with transitions
            if (!this.canChangeDeadline) return;
            return this.callTransitionTrigger(this.changeDeadlineTriggerName);
        },
        startChangeAssignedTus() {
            // start process of change assigned TUs transition -- do it with transitions
            if (!this.canChangeAssignedTus) return;
            return this.callTransitionTrigger(this.changeAssignedTusTriggerName);  // starts the process, shows the modal
        },
        startEmailAssignedTus() {
            // start process of calling trigger to email assigned tus (as the owner) 
            if (!this.canEmailAssignedTus) return;
            return this.callTransitionTrigger(this.emailAssignedTusTriggerName);
        },
        showAddExternalColleagueModal() {
            // show the modal, then that will handle calling the transition if needed
            this.addExternalColleagueModalShowing = true;
        },
        hideAddExternalColleagueModal() {
            // hide the modal, reset stuff 
            this.addExternalColleagueModalShowing = false;
            this.addExternalColleagueError = '';
            this.addExternalColleagueSelectedIds = [];  // maybe you want this on 'hidden' instead...
        },
        handleAddExternalColleagueSelected(selectedIds) {
            // selectedIds is array of TU.ids that are selected
            // filter out any that are already selected!
            this.addExternalColleagueSelectedIds = selectedIds.filter(tuId => !this.assignedTuIds.includes(tuId));
        },
        async triggerAddExternalColleague() {
            // called when selections are done, ready to do the thing
            // get IDs, make API call, show errors if needed
            // you only want to pass in the NEW ones - pass in using kwarg 'added_tu_ids'
            const addedTuIds = [ ...this.addExternalColleagueSelectedIds];
            if (addedTuIds.length < 1) {
                this.addExternalColleagueError = 'You have not selected any new users to add.';
            } else {
                // otherwise do the thing - not via transition component because that does the standard modal confirmation/form, and we want something very custom here
                this.addExternalColleagueLoading = true;
                const transition_data = {
                    'trigger': this.addExternalColleagueTriggerName,
                    // and all kwargs to pass to transition, except for 'tu' which is added automatically
                    'added_tu_ids': addedTuIds,
                };
                try {
                    console.log('added_tu_ids', addedTuIds);
                    const { data } = await suppliosApi.workflowTask.call_transition(this.taskId, transition_data);
                    // return data is object with { success (boolean), message, object (full serialized task), redirect_url (optional URL, might be '') }
                    // now hook into the same things you do after state-machine-transitions component does its thing...
                    // call onStateChange -- pass in full response data -- that's what that component does
                    this.onStateChange(data);  // that should handle everything
                } catch (error) {
                    this.apiErrorHandler(error);
                }
                this.addExternalColleagueLoading = false;
            }
        },
        async completeTask() {
            if (!this.canComplete) return;
            // SUBMIT FORMS FIRST
            if (this.isFormioForm) {
                const submitSuccess = await this.submitFormioForm();  // this is async!
                if (!submitSuccess) {
                    console.error(`Tried to submit formio form when completing workflow task ID ${this.taskId} but submitForm was not successful`);
                    return;  // show toast message?  maybe it was already shown from API error?
                }
            }
            if (this.isFormulateForm) {
                const submitSuccess = await this.submitFormulateForm();
                if (!submitSuccess) {
                    console.error(`Tried to submit formulate form when completing workflow task ID ${this.taskId} but submitForm was not successful`);
                    return;  // show toast message?  maybe it was already shown from API error?
                }
            }
            return this.callTransitionTrigger(this.completeTriggerName);
        },
        approvalTaskApprove() {
            if (!this.canApprovalApprove) return;
            return this.callTransitionTrigger(this.approvalTaskApproveTrigger);
        },
        approvalTaskReject() {
            if (!this.canApprovalReject) return;
            return this.callTransitionTrigger(this.approvalTaskRejectTrigger);
        },
        callWorkflowTransitionTrigger(triggerName) {
            // MAYBE YOU SHOULD NOT DO THIS... CALL IT THROUGH A TASK TRANSITION INSTEAD?  
            // Then you can 'complete' the Task at the same time, record the event linked to that task, etc. reset the workflow to there?
            // call the WORKFLOW-LEVEL state machine transition via state-machine-transitions component 
            // this handles showing the collectData modal, and calling it
            console.log(`hello from callWorkflowTransitionTrigger - will call ${triggerName}`);
            // this calls the transition using the StateMachineTransitions component via $refs
            // we must pass the entire transitionObject, the suppliosApi endpoint string, and the instance ID that we want to transition
            // so we have to look some things up first if we're only passing in the trigger name
            const transComponent = this.$refs['workflowTransitionsComponent'];
            if (!transComponent) return;
            // currently you are only showing SOME of the transitions... the 'featured' ones for the current step (which you define manually)
            const transitionObject = this.featuredWorkflowTransitions.find(trans => trans.trigger === triggerName);
            if (!transitionObject) {
                this.$toast.error('That action is not available.  Please contact Supplios Support.');
                return;
            }
            const callTransitionEndpoint = 'workflowRun.call_transition';
            transComponent.trigger(transitionObject, callTransitionEndpoint, this.workflowRunId);
            // then that component will emit 'state-change' with newState (string name of state)
            // handle that in other callback below
        },
        async afterWorkflowTransitionStateChange(wfStateChangeResp) {
            // called after the WORKFLOW state-change-transition is successful
            console.log(wfStateChangeResp);
            // HACK: we will call TASK transition to complete this task when workflow transition is successful
            if (this.isWorkflowTransitionTask) {
                await this.completeTask();
            }
            // no emit of state change here... wait until after this.completeTask() -- that will do it
        },
        callTransitionTrigger(triggerName) {  // not async... this will START the process of transition, first by showing confirmation modal
            console.log(`hello from callTransitionTrigger - will call ${triggerName}`);
            // this calls the transition using the StateMachineTransitions component via $refs
            // we must pass the entire transitionObject, the suppliosApi endpoint string, and the instance ID that we want to transition
            // so we have to look some things up first if we're only passing in the trigger name
            const transComponent = this.$refs['transitionsComponent'];
            if (!transComponent) return;
            const transitionObject = this.taskData.possible_transitions.find(trans => trans.trigger === triggerName);
            if (!transitionObject) {
                this.$toast.error('That action is not available.  Please contact Supplios Support.');
                return;
            }
            const callTransitionEndpoint = 'workflowTask.call_transition';
            transComponent.trigger(transitionObject, callTransitionEndpoint, this.taskId);
            // then that component will emit 'state-change' with newState (string name of state)
            // handle that in other callback below
        },
        onStateChange(stateChangeResponse) {
            // called when handling emitted 'state-change' event from StateMachineTransitions component (after it did the transition)
            // stateChangeResponse will be { success, message, object, redirect_url }
            const newTaskData = stateChangeResponse.object;
            console.log(`state changed - apiResponse below`);
            console.log(stateChangeResponse);
            // we will just reload things to update
            this.getDetail();
            // AND emit something to parent telling them they should probably update because state-changed
            this.$emit('state-changed', newTaskData);
            // also must emit @updated to tell parent list if we're in ManageResponses
        },
        handleWorkflowStateMachineLoading(loadingState) {
            this.loadingWorkflowTransition = loadingState;
        },
        handleStateMachineLoading(loadingState) {
            this.loadingTransition = loadingState;
        },
        onStateChangeCancel() {
            // called anytime the state change modal / process is cancelled
            // don't think we need to do anything here...
        },
        formatDate(dateValue) {
            return FormatDateHelper(dateValue);
        },
        makeFlag(flagObj) {  // 'constructs' custom flag objects -- adding in things from taskData
            //const openFlags = this.taskData?.open_flags;
            const possibleTransitions = this.taskData?.possible_transitions || [];
            const transitions = possibleTransitions.filter(trans => (trans.ui_data?.flag_key === flagObj.key));
            let flagTitle = flagObj.name || 'Attention';
            let userMessage = '';
            let colorVariant = 'warning';
            if (flagObj.key === 'dispute') {
                flagTitle = 'Dispute Raised';
                colorVariant = 'warning';
                userMessage = this.currentTuIsWorkflowOwner ?
                                'As workflow owner, you can either make changes to this issue, or reject this dispute.' : 
                                'Workflow owner has been notified.  Waiting on their action.';
            } else if (flagObj.key === 'rejection') {
                flagTitle = 'Rejected';
                colorVariant = 'danger';
                if (this.taskData?.is_approval_task) {
                    userMessage = this.currentTuIsAssignee ?
                                'A later approver has rejected this.  Review their comments and reject earlier tasks if needed.' : 
                                'Waiting on action from task assignee';
                } else {
                    userMessage = this.currentTuIsAssignee ?
                                'Please modify your submission and re-submit when ready.' : 
                                'Waiting on action from task assignee';
                }
            }
            return {...flagObj, transitions, colorVariant, userMessage, flagTitle }
        }
    }
}
</script>

<style lang="scss">

.task-header-toggle-button.collapsed .when-open,
.task-header-toggle-button.not-collapsed .when-closed {
  display: none;
}

</style>
